import React, { useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CheckCircleOutlined, SwapOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Space, Avatar, Typography } from 'antd';

import TrackerService from '../../utils/TrackerService';
import { formatTeamNameForDisplay } from '../../utils/claimOptionCopy';

import RedirectAndScrollTop from './RedirectAndScrollTop';
import { useClientDetails } from './useClientDetails';

const { Title, Text } = Typography;

function trackClaimSuccessClick(action, tag, client, clientId, extra = {}) {
  const tracker = new TrackerService({ tagId: tag?.id, object: tag });
  const payload = {
    ...TrackerService.buildClaimPayload(tag, client, client?.ownerId, { action, ...extra }),
  };
  tracker.mixpanelTrack('claim_success_clicked', payload);
  if (!tracker.state.isTest) tracker.googleTrack('claim_success_clicked', payload);
}

function rgbToHex(rgbString) {
  if (rgbString) {
    // Extract the values of r, g, and b from the rgbString
    const [r, g, b] = rgbString.substring(4, rgbString.length - 1).split(',').map(val => parseInt(val.trim()));

    // Convert the values to hex and concatenate them into a single string
    const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');

    // Prepend the '#' symbol to the hex string and return it
    return '#' + hex;
  }
  return null;
}

export const ClientPartnerConnectedCard = ({ client, partner, rgbColor, rgbColorClient }) => {
  const rgbForClient = rgbColorClient || rgbColor;
  return (
    <Space direction='horizontal' align='center'>
      <Avatar src={client.avatar} size='large' style={{ backgroundColor: rgbToHex(rgbForClient) }} icon={<UserOutlined />} alt={client.displayName} />
      <SwapOutlined />
      <Avatar src={partner.avatar} shape='square' size='large' style={{ backgroundColor: rgbToHex(rgbColor) }} icon={<BankOutlined />} alt={partner.displayName} />
    </Space>
  );
};

export const DataSharingLineItem = ({ id, item, description }) => {
  return <>
    {item && description &&
      <Space key={`sp-${id}`} direction='horizontal'>
        <p style={{ fontWeight: 'bold' }}>{`${item}`}</p>
        <p>{`(${description})`}</p>
      </Space>
    }
  </>
}

export const DataSharingList = ({ displayName, email, partnerDisplayName, tagUid }) => {
  const getDataSource = () => {
    let dataSource = [];

    if (displayName) {
      dataSource = [...dataSource, {
        id: 'li-share-name',
        item: displayName,
        description: 'name'
      }
      ];
    }

    if (email) {
      dataSource = [...dataSource, {
        id: 'li-share-email',
        item: email,
        description: 'email'
      }
      ];
    }

    if (tagUid && partnerDisplayName) {
      dataSource = [...dataSource, {
        id: 'li-share-tag',
        item: tagUid,
        description: `${partnerDisplayName} ID`
      }
      ];
    }

    if (partnerDisplayName) {
      dataSource = [...dataSource, {
        id: 'li-share-payments',
        item: 'Payments',
        description: `using ${partnerDisplayName} links`
      }
      ];
    }

    return dataSource;
  }

  const listItems = getDataSource().map((item) =>
    <DataSharingLineItem key={item.id} {...item} />
  );

  return <>
    <Space direction='vertical' size={1}>
      {listItems}
    </Space>
  </>
}

export const SharingDataWithPartnerCard = ({ partnerClientId, clientId, tagId, tagUid }) => {
  const client = useClientDetails(clientId);
  const partner = useClientDetails(partnerClientId);

  const partnerDisplayName = partner ? partner.displayName : null;
  const { displayName, email } = client ?? {};

  return (
    <>
      {partner && client &&
        <Card type="inner" >
          <Card.Meta
            title={`Data shared with ${partnerDisplayName}`}
            description={'Sign up & Payments'}
          />
          <Divider />
          <DataSharingList partnerDisplayName={partnerDisplayName} email={email} displayName={displayName} tagUid={tagUid} />
        </Card>
      }
    </>
  );
};

export const ReturnToPartnerButton = ({ templateTagId, tagId, tag, clientId }) => {
  const { signupConfirmUrl, partnerClientId, tagUid, webhookConfig, is_test, rgbColor } = tag;
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const resultStatus = 'live';

  const client = useClientDetails(clientId);
  const partner = useClientDetails(partnerClientId);

  const mapPartnerData = (partner) => {
    const { displayName } = partner ?? {};
    return { partnerDisplayName: displayName };
  };

  const mapClientData = (client) => {
    const { displayName } = client ?? {};
    return { displayName };
  };

  const MakeSignupConfirmUrl = (signupConfirmUrl) => {
    if (signupConfirmUrl) {
      const idParam = tagUid ? `uid=${tagUid}` : `id=${tagId}`;
      return `${signupConfirmUrl}?${idParam}&status=${resultStatus}&template=${templateTagId}`;
    }

    return signupConfirmUrl;
  };

  const { displayName } = mapClientData(client);
  const { partnerDisplayName } = mapPartnerData(partner);

  const fullSignupConfirmUrl = MakeSignupConfirmUrl(signupConfirmUrl);
  const returnButtonText = partnerDisplayName ? `Return to ${partnerDisplayName}` : null;

  const tracker = new TrackerService({ tagId, object: tag });
  const data = {
    ...TrackerService.buildClaimPayload(tag, client, client?.ownerId, {
      displayName,
      partnerDisplayName,
      tagUid,
      signupConfirmUrl,
      fullSignupConfirmUrl,
      returnButtonText,
      webhookConfig,
      status: resultStatus,
    }),
  };

  const onClickReturnToPartner = async () => {
    trackClaimSuccessClick('return_to_partner', tag, client, clientId, data);
    tracker.mixpanelTrack('claim_return_to_partner', data);

    if (!tracker.state.isTest) {
      tracker.googleTrack('claim_return_to_partner', data);
    }

    if (signupConfirmUrl) {
      setShouldRedirect(true);
    }
  };

  return (
    <Space direction='vertical' align='center'>
      <RedirectAndScrollTop
        when={shouldRedirect}
        to={fullSignupConfirmUrl}
        pushBefore={'/'}
      />
      {returnButtonText && fullSignupConfirmUrl &&
        <Button
          key={`tag-cliam-return-button-${templateTagId}`}
          type='primary'
          onClick={onClickReturnToPartner}
          icon={<CheckCircleOutlined />}
          style={{ backgroundColor: '#27AE60' }}
          disabled={shouldRedirect}
        >
          {returnButtonText}
        </Button>
      }
      {is_test &&
        <p style={{ color: 'red', fontSize: 10, padding: '15px' }}>{`${fullSignupConfirmUrl}`}</p>
      }
    </Space>
  );
};

export const ClaimTagSuccessCard = ({ templateTagId, tagId, tag, clientId, isAdmin, locStrings, ...rest }) => {
  const { tagUid, partnerClientId, rgbColor } = tag;
  const client = useClientDetails(clientId);
  const partner = useClientDetails(partnerClientId);
  const { rgbColor: rgbColorClient } = client || {};

  return (
    <>
      {client && partner &&
        <Card>
          <Space direction='vertical' align='center'>
            <p>{`You are now connected to ${partner.displayName}`}</p>

            <ClientPartnerConnectedCard
              client={client}
              partner={partner}
              rgbColor={rgbColor}
              rgbColorClient={rgbColorClient}
            />

            <ReturnToPartnerButton
              templateTagId={templateTagId}
              tagId={tagId}
              tag={tag}
              clientId={clientId}
            />

            <SharingDataWithPartnerCard
              clientId={clientId}
              partnerClientId={partnerClientId}
              tagId={tagId}
              tagUid={tagUid}
            />

            <Link
              to={`/client/manage/${clientId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button key='confirm' onClick={() => trackClaimSuccessClick('dashboard', tag, client, clientId)}>
                {'Go to Strikepay Dashboard'}
              </Button>
            </Link>
          </Space>
        </Card>
      }
    </>
  );
};

export const PersonalTagClaimSuccessCard = ({ teamId, teamName, isNewTeam, templateTagId, tagId, tag, clientId, client, isAdmin, locStrings, waitingOnRequest, copy, ...rest }) => {
  const isTeamClaim = !!teamId;
  const displayTeamName = formatTeamNameForDisplay(teamName);
  const successCopy = copy?.success;

  const buttonLabel = successCopy
    ? successCopy.getButtonLabel(displayTeamName)
    : (isTeamClaim ? `Open ${displayTeamName || 'Team'}` : 'Go to your account');

  const bodyText = successCopy
    ? successCopy.getBody(displayTeamName)
    : (isTeamClaim
      ? `Tips from this QR code will now go to ${displayTeamName || 'this team'}.`
      : 'Tips from this QR code will now go to you.');

  const bodyContent = isTeamClaim && bodyText ? (() => {
    const target = String(displayTeamName || '').trim() || 'this team';
    const parts = bodyText.split(target);
    if (parts.length === 2) {
      return <>{parts[0]}<Text strong>{target}</Text>{parts[1]}</>;
    }
    return bodyText;
  })() : bodyText;

  const dashboardPath = `/client/manage/${clientId}`;
  const teamPath = isTeamClaim ? `/client/manage/${clientId}/teams/${teamId}` : null;
  /** Team settings screen + hash: TeamSettingsBody uses `#outer-inner` (e.g. general-image = Team > Branding, general-location = Team > Location & contact). */
  const teamSettingsPath = teamPath ? `${teamPath}/settings` : null;

  const history = useHistory();
  const handleClick = useCallback(() => {
    if (teamPath) {
      history.push(dashboardPath);
      history.push(teamPath);
    } else {
      history.push(dashboardPath);
    }
  }, [history, dashboardPath, teamPath]);

  /**
   * Open manage/team links in a new tab so the success screen stays open.
   * A fresh tab has no history stack — Back is a non-issue (often closes the tab or is a no-op).
   */
  const openManageInNewTab = useCallback((pathWithOptionalHash) => {
    if (!pathWithOptionalHash) return;
    const path = pathWithOptionalHash.startsWith('/') ? pathWithOptionalHash : `/${pathWithOptionalHash}`;
    window.open(`${window.location.origin}${path}`, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Card loading={waitingOnRequest} style={{ maxWidth: 440, margin: '16px auto', textAlign: 'center', borderRadius: 12 }}>
      <Space direction='vertical' align='center' size='middle' style={{ width: '100%', padding: '16px 0' }}>
        <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
        <Title level={3} style={{ margin: 0, color: '#52c41a', fontSize: 22 }}>{successCopy?.title ?? "You're all set"}</Title>
        <Text style={{ fontSize: 15 }}>{bodyContent}</Text>
        {isTeamClaim && successCopy?.whatToDoNext && successCopy?.nextSteps && (
          <>
            <Title level={5} style={{ marginTop: 8, marginBottom: 8, fontWeight: 600, fontSize: 15 }}>
              {successCopy.whatToDoNext}
            </Title>
            <Space direction='vertical' size={8} style={{ width: '100%', maxWidth: 360 }}>
              {(tag?.actionText?.postClaimTitle || tag?.actionText?.postClaimText || tag?.actionText?.postClaimLink) && (
                <Card
                  size='small'
                  hoverable={!!tag?.actionText?.postClaimLink}
                  style={{
                    borderRadius: 8,
                    border: '1px solid #e8e8e8',
                    textAlign: 'left',
                    cursor: tag?.actionText?.postClaimLink ? 'pointer' : undefined,
                    width: '100%',
                  }}
                  onClick={tag?.actionText?.postClaimLink ? () => {
                    trackClaimSuccessClick('post_claim', tag, client, clientId, { postClaimLink: tag.actionText.postClaimLink });
                    window.open(tag.actionText.postClaimLink, '_blank', 'noopener,noreferrer');
                  } : undefined}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div>
                  {tag?.actionText?.postClaimTitle && (
                    <Text strong style={{ display: 'block' }}>{tag.actionText.postClaimTitle}</Text>
                  )}
                  {tag?.actionText?.postClaimText && (
                    <Text type='secondary' style={{ display: 'block', fontSize: 13 }}>{tag.actionText.postClaimText}</Text>
                  )}
                  {tag?.actionText?.postClaimLink && (
                    <Text style={{ color: '#1890ff', fontWeight: 500, display: 'block', marginTop: 4 }}>
                      {tag.actionText.postClaimLinkText || 'More info...'}
                    </Text>
                  )}
                </div>
                </Card>
              )}
              {teamSettingsPath && (
                <Card
                  size='small'
                  hoverable
                  onClick={() => {
                    const dest = `${teamSettingsPath}#general-image`;
                    trackClaimSuccessClick('branding', tag, client, clientId, { targetPath: dest });
                    openManageInNewTab(dest);
                  }}
                  style={{ borderRadius: 8, border: '1px solid #e8e8e8', textAlign: 'left', cursor: 'pointer' }}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div>
                    <Text strong style={{ display: 'block' }}>{successCopy.nextSteps.branding.title}</Text>
                    <Text type='secondary' style={{ display: 'block', fontSize: 13 }}>{successCopy.nextSteps.branding.description}</Text>
                    <Text style={{ color: '#1890ff', fontWeight: 500, display: 'block', marginTop: 4 }}>{successCopy.nextSteps.branding.actionLabel}</Text>
                  </div>
                </Card>
              )}
              {teamPath && (
                <Card
                  size='small'
                  hoverable
                  onClick={() => {
                    trackClaimSuccessClick('invite', tag, client, clientId, { targetPath: teamPath });
                    openManageInNewTab(teamPath);
                  }}
                  style={{ borderRadius: 8, border: '1px solid #e8e8e8', textAlign: 'left', cursor: 'pointer' }}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div>
                    <Text strong style={{ display: 'block' }}>{successCopy.nextSteps.invite.title}</Text>
                    <Text type='secondary' style={{ display: 'block', fontSize: 13 }}>{successCopy.nextSteps.invite.description}</Text>
                    <Text style={{ color: '#1890ff', fontWeight: 500, display: 'block', marginTop: 4 }}>{successCopy.nextSteps.invite.actionLabel}</Text>
                  </div>
                </Card>
              )}
              {teamSettingsPath && (
                <Card
                  size='small'
                  hoverable
                  onClick={() => {
                    const dest = `${teamSettingsPath}#actions`;
                    trackClaimSuccessClick('thank_you', tag, client, clientId, { targetPath: dest });
                    openManageInNewTab(dest);
                  }}
                  style={{ borderRadius: 8, border: '1px solid #e8e8e8', textAlign: 'left', cursor: 'pointer' }}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div>
                    <Text strong style={{ display: 'block' }}>{successCopy.nextSteps.thankYouPage.title}</Text>
                    <Text type='secondary' style={{ display: 'block', fontSize: 13 }}>{successCopy.nextSteps.thankYouPage.description}</Text>
                    <Text style={{ color: '#1890ff', fontWeight: 500, display: 'block', marginTop: 4 }}>{successCopy.nextSteps.thankYouPage.actionLabel}</Text>
                  </div>
                </Card>
              )}
            </Space>
          </>
        )}
        <Button
          type='primary'
          size='large'
          style={{ borderRadius: 8, minWidth: 220, height: 48, fontSize: 16, fontWeight: 600, marginTop: 8 }}
          disabled={waitingOnRequest}
          onClick={() => {
            trackClaimSuccessClick(isTeamClaim ? 'open_team' : 'open_account', tag, client, clientId, { teamId, teamPath, dashboardPath });
            handleClick();
          }}
        >
          {buttonLabel}
        </Button>
      </Space>
    </Card>
  );
}
